import { useState, useCallback, useEffect } from 'react'
import { useWalletClient, useSwitchChain, useChainId, useAccount } from 'wagmi'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { ethers } from 'ethers'
import { getEthersSignerFromWalletClient } from '../services/wallet'
import { submitCustodyOnChainComplete, withdrawCustodyOnChainPending } from '../services/api'
import { useBeginWalletConnect } from '../contexts/WalletConnectReminderContext'

const M1NT_CARD_ABI = [
  'function safeTransferFrom(address from, address to, uint256 id, uint256 amount, bytes data) external',
]

const FALLBACK_CONTRACT = import.meta.env.VITE_M1NT_CARD_ADDRESS || ''

function tryChecksum(addr) {
  if (!addr || typeof addr !== 'string') return null
  try {
    return ethers.getAddress(addr.trim())
  } catch {
    return null
  }
}

/** Ethers / RPC often surface failed simulations as this opaque message. */
function formatCustodyTransferError(err) {
  const fromApi = err?.response?.data?.error
  const raw = String(fromApi || err?.shortMessage || err?.message || '').trim()
  if (!raw) return 'Transfer failed.'
  if (/missing revert data/i.test(raw) || raw.includes('CALL_EXCEPTION')) {
    return (
      'This transaction would fail on-chain (the network did not return a reason). ' +
      'Use the chain ID shown below, connect the wallet that matches your M1NT profile and that actually holds this NFT, ' +
      'and ensure you have enough native token for gas.'
    )
  }
  return raw
}

/**
 * Banner for the prior owner: complete ERC-1155 transfer after custody approval (English UI).
 */
export default function CustodyOnChainHandoff({ items = [], meta = {}, wallet, onComplete }) {
  const beginWalletConnect = useBeginWalletConnect()
  const { address } = useAccount()
  const { data: walletClient } = useWalletClient()
  const { switchChainAsync } = useSwitchChain()
  const currentChainId = useChainId()
  /** `${requestId}:send` | `${requestId}:withdraw` */
  const [busyKey, setBusyKey] = useState(null)
  const [error, setError] = useState(null)
  const [estimateById, setEstimateById] = useState({})
  /** Item pending confirmation before calling withdraw API */
  const [withdrawConfirmItem, setWithdrawConfirmItem] = useState(null)

  const contractAddress = meta.contractAddress || FALLBACK_CONTRACT
  const targetChainId = meta.chainId ?? parseInt(import.meta.env.VITE_CHAIN_ID || '80002', 10)

  const profileWallet = tryChecksum(wallet)
  const connectedWallet = tryChecksum(address)
  const walletMismatch = Boolean(
    profileWallet &&
    connectedWallet &&
    profileWallet.toLowerCase() !== connectedWallet.toLowerCase()
  )
  const needsConnect = items.length > 0 && !connectedWallet

  const estimateFee = useCallback(
    async (item) => {
      if (!walletClient || !contractAddress || !address || item.card?.tokenId == null) return
      setError(null)
      try {
        if (targetChainId && currentChainId !== targetChainId) {
          setEstimateById((m) => ({ ...m, [item.id]: 'Switch network first' }))
          return
        }
        const signer = await getEthersSignerFromWalletClient(walletClient)
        if (!signer) throw new Error('No signer')
        const c = new ethers.Contract(contractAddress, M1NT_CARD_ABI, signer)
        const to = ethers.getAddress(item.newOwnerWallet.trim())
        const from = ethers.getAddress(address)
        const tid = BigInt(item.card.tokenId)
        const gas = await c.safeTransferFrom.estimateGas(from, to, tid, 1n, '0x')
        const prov = signer.provider
        const fee = await prov.getFeeData()
        const gasPrice = fee.gasPrice ?? fee.maxFeePerGas ?? 0n
        const costWei = gas * gasPrice
        const label = `${ethers.formatEther(costWei)} native (est.)`
        setEstimateById((m) => ({ ...m, [item.id]: label }))
      } catch (e) {
        console.warn('Gas estimate failed:', e)
        setEstimateById((m) => ({
          ...m,
          [item.id]: formatCustodyTransferError(e),
        }))
      }
    },
    [walletClient, contractAddress, address, currentChainId, targetChainId]
  )

  const sendTransfer = async (item) => {
    if (!walletClient || !contractAddress) {
      setError('Wallet or contract not ready.')
      return
    }
    if (needsConnect) {
      setError('Connect your wallet in the header, then try again.')
      return
    }
    if (walletMismatch) {
      setError(
        `Connect the same wallet as your M1NT profile (${profileWallet}). The NFT is sent from that address; you currently have ${connectedWallet} selected.`
      )
      return
    }
    setError(null)
    setBusyKey(`${item.id}:send`)
    try {
      if (targetChainId && currentChainId !== targetChainId) {
        await switchChainAsync({ chainId: targetChainId })
      }
      const signer = await getEthersSignerFromWalletClient(walletClient)
      if (!signer) throw new Error('Could not get wallet signer.')
      const from = ethers.getAddress(address)
      const to = ethers.getAddress(item.newOwnerWallet.trim())
      const tid = BigInt(item.card.tokenId)
      const c = new ethers.Contract(contractAddress, M1NT_CARD_ABI, signer)
      const tx = await c.safeTransferFrom(from, to, tid, 1n, '0x')
      const receipt = await tx.wait()
      if (Number(receipt?.status) !== 1) throw new Error('Transaction reverted')
      await submitCustodyOnChainComplete(item.id, receipt.hash, wallet)
      onComplete?.()
    } catch (e) {
      console.error(e)
      setError(formatCustodyTransferError(e))
    } finally {
      setBusyKey(null)
    }
  }

  const runWithdraw = async (item) => {
    if (!wallet) {
      setError('Connect the wallet that held this NFT before the custody move.')
      setWithdrawConfirmItem(null)
      return
    }
    setWithdrawConfirmItem(null)
    setError(null)
    setBusyKey(`${item.id}:withdraw`)
    try {
      await withdrawCustodyOnChainPending(item.id, wallet)
      onComplete?.()
    } catch (e) {
      console.error(e)
      setError(e?.response?.data?.error || e?.message || 'Could not cancel handoff')
    } finally {
      setBusyKey(null)
    }
  }

  useEffect(() => {
    if (!withdrawConfirmItem) return
    const onKey = (e) => {
      if (e.key === 'Escape') setWithdrawConfirmItem(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [withdrawConfirmItem])

  if (!items.length) return null

  return (
    <div
      className="mb-12 rounded-2xl border border-m1-amber/35 bg-m1-amber/10 p-5 sm:mb-16 sm:p-7"
      role="region"
      aria-label="Complete on-chain custody transfer"
    >
      <div className="mb-4 flex items-start gap-3">
        <i
          className="ri-error-warning-line mt-0.5 flex-shrink-0 text-2xl text-m1-amber"
          aria-hidden="true"
        />
        <div>
          <h2 className="font-m1-display text-[clamp(24px,3vw,32px)] font-normal leading-tight text-m1-ink">
            Complete on-chain transfer
          </h2>
          <p className="mt-2 max-w-[40em] text-[16px] font-light leading-relaxed text-m1-ink-soft">
            You approved a custody move. Send one blockchain transaction from your wallet to move
            the NFT to the new owner. You only pay the network fee (gas), not a fee to M1NT. After
            this, OpenSea should show the new owner.
          </p>
        </div>
      </div>
      {!contractAddress && (
        <p className="mb-3 text-[15px] text-m1-amber">
          M1NT Card contract address is not configured. Contact support.
        </p>
      )}
      {profileWallet && (
        <p className="mb-3 break-all text-[13px] font-light text-m1-muted">
          M1NT profile wallet (must hold this NFT and match the connected account):{' '}
          <span className="font-m1-mono text-m1-ink-soft">{profileWallet}</span>
        </p>
      )}
      {walletMismatch && (
        <p className="mb-3 break-words text-[15px] text-m1-amber" role="status">
          Your connected wallet does not match your M1NT profile wallet. Switch accounts in your
          wallet extension, then use Send transfer.
        </p>
      )}
      {needsConnect && (
        <div
          className="mb-4 rounded-2xl border border-m1-violet/35 bg-m1-ground/60 p-4 sm:p-5"
          role="status"
          aria-label="Connect wallet to sign"
        >
          <p className="mb-1 text-[15px] font-light leading-relaxed text-m1-ink-soft">
            If you signed in with email, your browser is not linked to MetaMask yet. Connect below
            so this page can request the transfer. In the wallet picker, choose the account that
            matches your M1NT profile
            {profileWallet ? (
              <>
                {' '}
                (<span className="font-m1-mono text-m1-ink">{profileWallet}</span>)
              </>
            ) : (
              ''
            )}
            .
          </p>
          <p className="mb-4 text-[13px] text-m1-amber">
            Having MetaMask open is not enough — the site must connect to it for this tab.
          </p>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button
                type="button"
                onClick={() => beginWalletConnect(openConnectModal)}
                className="inline-flex min-h-[44px] items-center justify-center border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/30 disabled:hover:text-m1-ink"
              >
                Connect wallet in this browser
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {error && (
        <p className="mb-3 break-words text-[15px] text-m1-amber" role="alert">
          {error}
        </p>
      )}
      <ul>
        {items.map((item) => (
          <li
            key={item.id}
            className="flex flex-col gap-4 border-t border-m1-line py-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="truncate text-[18px] text-m1-ink">{item.card?.name || 'Card'}</p>
              <p className="mt-1 break-all font-m1-mono text-[12px] text-m1-muted">
                To: {item.newOwnerWallet}
              </p>
              <p className="mt-0.5 font-m1-mono text-[12px] text-m1-ink-soft">
                Token ID #{item.card?.tokenId}
              </p>
              {estimateById[item.id] && (
                <p className="mt-1 text-[13px] text-m1-amber">
                  Est. network cost: {estimateById[item.id]}
                </p>
              )}
            </div>
            <div className="flex w-full flex-shrink-0 flex-col gap-2 sm:w-auto sm:items-end">
              <button
                type="button"
                onClick={() => estimateFee(item)}
                disabled={
                  !contractAddress ||
                  busyKey?.startsWith(`${item.id}:`) ||
                  needsConnect ||
                  walletMismatch
                }
                className="inline-flex min-h-[44px] items-center justify-center border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/30 disabled:hover:text-m1-ink w-full sm:w-auto"
              >
                Estimate network fee
              </button>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <button
                  type="button"
                  onClick={() => setWithdrawConfirmItem(item)}
                  disabled={busyKey?.startsWith(`${item.id}:`)}
                  className="inline-flex min-h-[44px] items-center justify-center border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/30 disabled:hover:text-m1-ink order-2 sm:order-1"
                >
                  {busyKey === `${item.id}:withdraw` ? 'Cancelling…' : 'Cancel transfer'}
                </button>
                <button
                  type="button"
                  onClick={() => sendTransfer(item)}
                  disabled={
                    !contractAddress ||
                    busyKey?.startsWith(`${item.id}:`) ||
                    needsConnect ||
                    walletMismatch
                  }
                  className="inline-flex min-h-[44px] items-center justify-center border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/30 disabled:hover:text-m1-ink order-1 border-m1-violet text-m1-violet sm:order-2"
                >
                  {busyKey === `${item.id}:send` ? 'Confirm in wallet…' : 'Send transfer'}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[13px] text-m1-muted">
        Wrong network? Switch to chain ID {targetChainId} in your wallet before sending.
      </p>

      {withdrawConfirmItem && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          onClick={() => !busyKey && setWithdrawConfirmItem(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="custody-withdraw-title"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-m1-amber/35 bg-m1-surface p-5 shadow-[0_25px_50px_rgba(0,0,0,0.5)] sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-start gap-3">
              <i
                className="ri-error-warning-line mt-0.5 flex-shrink-0 text-2xl text-m1-amber"
                aria-hidden="true"
              />
              <div>
                <h3
                  id="custody-withdraw-title"
                  className="font-m1-display text-[26px] font-normal leading-tight text-m1-ink"
                >
                  Cancel this handoff?
                </h3>
                <p className="mt-3 text-[15px] font-light leading-relaxed text-m1-ink-soft">
                  The card will return to your M1NT account. The other party will no longer see it
                  in their collection, and no on-chain transfer will be recorded. You can ask the
                  Lab to start a new transfer later if needed.
                </p>
                {withdrawConfirmItem.card?.name && (
                  <p className="mt-3 truncate text-[14px] text-m1-muted">
                    {withdrawConfirmItem.card.name}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
              <button
                type="button"
                onClick={() => setWithdrawConfirmItem(null)}
                disabled={!!busyKey}
                className="inline-flex min-h-[44px] items-center justify-center border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/30 disabled:hover:text-m1-ink"
              >
                Keep handoff
              </button>
              <button
                type="button"
                onClick={() => runWithdraw(withdrawConfirmItem)}
                disabled={!!busyKey}
                className="inline-flex min-h-[44px] items-center justify-center border border-white/30 px-5 text-[14px] tracking-[0.04em] text-m1-ink transition-colors hover:border-m1-violet hover:text-m1-violet disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/30 disabled:hover:text-m1-ink border-m1-violet text-m1-violet"
              >
                {busyKey === `${withdrawConfirmItem.id}:withdraw`
                  ? 'Cancelling…'
                  : 'Yes, cancel handoff'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
